<template>
	<div v-if="outlet" class="px-sm md:px-md lg:px-sm">
		<v-container class="mx-auto grid grid-cols-12 py-lg">
			<div class="col-span-full space-y-lg">
				<v-breadcrumbs></v-breadcrumbs>

				<v-h2 tag="h1" class="uppercase">
					<span v-if="currentLang === 'fi'" class="relative text-5xl">
						{{ t("outlet-prefix") }} &nbsp;
					</span>

					<br />

					<span class="relative">
						{{ outlet.name }}
					</span>
				</v-h2>
			</div>
		</v-container>

		<v-container class="md:grid md:auto-rows-auto md:grid-cols-12 md:items-end">
			<!--
            Outlet images
            Row 1, col 1 - 7
            -->
			<div
				class="col-span-6 row-span-2 mt-lg pb-sm md:sticky md:bottom-0 md:pb-0"
			>
				<div class="md:min-h-[calc(100vh-1.5rem)]">
					<v-page-outlets-outlet-gallery
						:assets="outlet.content.outlet_images"
						class="md:sticky md:top-lg"
					/>
				</div>
			</div>

			<!-- Outlet information configuration:
                1. Contact information
                2. Outlet content
                3. Disclosure menus
            -->

			<div
				class="col-span-5 col-start-8 row-start-1 px-md pb-lg md:sticky md:top-0 md:mt-xl md:px-0"
			>
				<div
					v-if="
						currentLang === 'fi' &&
						(outlet.content.warehouse_pickup || outlet.content.trailer_rental)
					"
					class="flex flex-col justify-between space-y-sm rounded-t-2xs border border-b-0 border-solid border-gray-100 p-sm"
				>
					<div
						v-if="outlet.content.trailer_rental"
						class="flex flex-row items-center"
					>
						<v-icon icon="trailer" class="mr-sm text-2xl text-green-600" />

						<p>{{ t("trailer-rental") }}</p>
					</div>

					<div
						v-if="outlet.content.warehouse_pickup"
						class="flex flex-row items-center"
					>
						<v-icon icon="warehouse" class="mr-sm text-green-600" />

						<p>{{ t("warehouse-ingress") }}</p>
					</div>
				</div>

				<div
					v-if="ctaBlok"
					class="flex flex-col justify-between space-y-sm rounded-t-2xs border border-b-0 border-solid border-gray-100 p-sm"
				>
					<div
						class="w-1/2 rounded-2xs bg-(--background-color) p-sm text-(--text-color)"
						:style="{
							'--background-color': ctaBlok.button_color?.color || 'white',
							'--text-color': ctaTextColor,
						}"
					>
						<nuxt-link :to="formatSbUrl(ctaBlok.link)" class="space-x-sm">
							<p class="text-center font-bold">{{ ctaBlok?.link_text }}</p>
						</nuxt-link>
					</div>
				</div>

				<div
					class="flex flex-row justify-between rounded-t-2xs border border-b-0 border-solid border-gray-100 p-sm"
				>
					<div class="flex flex-row items-center">
						<v-icon icon="location" class="mr-sm" />

						<p class="font-bold">{{ t("address") }}</p>
					</div>

					<p class="space-x-md">
						{{ outlet.content.contact }}, {{ outlet.content.city }}
					</p>
				</div>

				<div
					class="flex flex-row justify-between border border-solid border-gray-100 p-sm"
				>
					<div class="flex flex-row items-center">
						<v-icon icon="call" class="mr-sm" />

						<p class="font-bold">{{ t("phone-number") }}</p>
					</div>

					<a :href="`tel:${outlet.content.phone}`" class="hover:underline">
						{{ outlet.content.phone }}
					</a>
				</div>

				<div
					v-if="outlet.content.email && outlet.content.email.length > 1"
					class="flex flex-row justify-between border border-t-0 border-solid border-gray-100 p-sm"
				>
					<div class="flex flex-row items-center">
						<v-icon icon="at" class="mr-sm" />

						<p class="font-bold">{{ t("email") }}</p>
					</div>

					<a :href="`mailto:${outlet.content.email}`" class="hover:underline">
						{{ outlet.content.email }}
					</a>
				</div>

				<div
					class="flex flex-col justify-between rounded-b-2xs border border-t-0 border-solid border-gray-100 p-sm"
				>
					<div class="flex flex-row items-center">
						<v-icon icon="clock" class="mr-sm" />
						<!-- {{ outlet.content.hours_table }} -->

						<p class="font-bold">{{ t("outlet-hours") }}</p>
					</div>

					<div
						v-if="hoursRows.length > 0 || outlet.content.hours"
						class="my-sm flex flex-col items-start space-y-sm pl-lg"
					>
						<dl v-if="hoursRows.length > 0" class="grid grid-cols-2 gap-x-lg">
							<template v-for="(tr, index) in hoursRows" :key="index">
								<dt
									:class="{
										'font-bold': tr.body[0]?.value === currentDayUpperCase,
									}"
								>
									{{ tr.body[0]?.value }}
								</dt>

								<dd
									:class="{
										'font-bold': tr.body[0]?.value === currentDayUpperCase,
									}"
								>
									{{ tr.body[1]?.value }}
								</dd>
							</template>
						</dl>
					</div>

					<div class="pl-lg">
						<!--- Exception hours -->
						<p
							v-if="(outlet.content.hours.content?.length ?? 0) > 0"
							class="font-bold"
						>
							{{ t("exceptions") }}
						</p>

						<sb-rich-text
							:document="outlet.content.hours"
							class="prose prose-sm font-bold wrap-break-word"
						/>
					</div>

					<div v-if="outlet.content.warehouse_pickup" class="pt-lg">
						<div
							v-if="outlet.content.pickup_available"
							class="flex flex-row items-center"
						>
							<v-icon icon="clock" class="mr-sm text-2xl text-black" />

							<p class="font-bold">{{ t("warehouse-hours") }}</p>
						</div>

						<div class="pl-lg">
							<p class="font-bold">
								{{ t("outlet-warehouse-hours") }}
							</p>

							<sb-rich-text
								:document="outlet.content.warehouse_hours"
								class="prose prose-sm wrap-break-word lg:prose-lg xl:prose-xl"
							/>
						</div>
					</div>
				</div>

				<div
					v-if="outlet.content.languages.length > 0"
					class="flex flex-col justify-between rounded-b-2xs border border-t-0 border-solid border-gray-100 p-sm"
				>
					<div class="flex flex-row items-center">
						<v-icon icon="message-text" class="mr-sm text-black" />

						<p class="font-bold">Palvelemme</p>
					</div>

					<div
						v-for="(lang, index) in outlet.content.languages"
						:key="lang._uid || index"
						class="pl-lg"
					>
						<sb-languages :blok="lang"> </sb-languages>
					</div>
				</div>

				<div
					v-if="outlet.content.socials?.length"
					class="flex flex-col justify-between rounded-b-2xs border border-t-0 border-solid border-gray-100 p-sm"
				>
					<div class="flex flex-row items-center">
						<v-icon icon="linear-message" class="mr-sm text-black" />

						<p class="font-bold">
							{{ t("socials-title") }}
						</p>
					</div>

					<div class="flex flex-col pl-lg">
						<template
							v-for="(social, index) in outlet.content.socials"
							:key="index"
						>
							<component :is="social.component" :blok="social" />
						</template>
					</div>
				</div>

				<!-- Description -->
				<div
					v-if="outlet.content.outlet_body"
					class="prose pt-lg wrap-break-word"
				>
					<sb-rich-text :document="outlet.content.outlet_body" />
				</div>

				<!-- Outlet warehouse information -->
				<div v-if="outlet.content.warehouse_pickup">
					<v-disclosure>
						<template #button>
							<p class="font-bold">{{ t("warehouse-ingress") }}</p>
						</template>

						<template #content>
							<div class="prose space-y-md wrap-break-word">
								<sb-rich-text
									:document="outlet.content.warehouse_body"
									class="prose"
								/>

								<sb-rich-text
									:document="outlet.content.warehouse_hours"
									class="prose"
								/>
							</div>
						</template>
					</v-disclosure>
				</div>
				<!-- Outlet location -->
				<div>
					<v-page-outlets-disclosure is-open>
						<template #button>
							<p class="font-bold">{{ t("outlet-location") }}</p>
						</template>

						<template #content>
							<v-page-outlets-outlet-map-new
								:location="outlet.content.location"
								:markers="markers"
								:placeholder-image-src="mapPlaceholderImageSrc"
							/>
						</template>
					</v-page-outlets-disclosure>
				</div>
			</div>
		</v-container>
	</div>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "storyblok-js-client";
import {parse, format} from "date-fns";
import type {MapMarker} from "@pkg/shared/types/components/maps";
import type {BlokComponent, Coordinates} from "@pkg/shared/types";

import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";

const slug = useRoute().params.outlet;

const {t} = useI18n();
const {t: tg, locale} = useI18n({
	useScope: "global",
});

definePageMeta({
	alias: ["/kauplused/:outlet", "/myymalat/:outlet"],
});

const {$i18n} = useNuxtApp();
const currentLang = computed(() => $i18n.locale.value);

type OutletStory = ISbStoryData<
	Extract<BlokComponent, {component: "sb-page-outlet-ktrl"}>
>;
const outlet = ref<OutletStory>();

const markers = computed<MapMarker[]>(() =>
	outlet.value?.content?.location
		? [
				{
					coordinates: outlet.value?.content?.location,
					title: outlet.value?.content?.name,
					content: outlet.value, // Add the required content property
				},
			]
		: [],
);

try {
	const {
		data: {value: story},
	} = await useStoryblokStory(`${tg("storyblok.outlets")}/${slug}`);

	if (!story) {
		throw new Error("Page not found");
	}

	outlet.value = story as OutletStory;
} catch {
	throw createError({statusCode: 404, statusMessage: "Page Not Found"});
}

let mapPlaceholderImageSrc: string | undefined;
try {
	const {
		data: {value: outletsInfo},
	} = await useStoryblokStory(tg("storyblok.outlets"));
	mapPlaceholderImageSrc =
		outletsInfo?.content?.map_placeholder_image?.filename;
} catch {
	// Non-critical — map placeholder simply won't show
}

const hoursBody = computed(() => outlet?.value?.content.hours_table);
const hoursRows = computed(() => hoursBody.value?.tbody ?? []);

const currentDay = new Intl.DateTimeFormat(t("date-locale"), {
	weekday: "long",
}).format();
const currentDayUpperCase =
	currentDay.charAt(0).toUpperCase() + currentDay.slice(1);

const ctaBlok = computed(() => outlet.value?.content.cta_blok?.[0]);
const ctaTextColor = computed(() => {
	const textColor = ctaBlok.value?.text_color;
	return typeof textColor === "string"
		? textColor
		: textColor?.color || "black";
});

// https://schema.org/openingHours
// Days are specified using the following two-letter english combinations: Mo, Tu, We, Th, Fr, Sa, Su.
const dayNameMapping = {
	fi: {
		Maanantai: "Mo",

		Tiistai: "Ti",

		Keskiviiko: "We",

		Torstai: "Th",

		Perjantai: "Fr",

		Lauantai: "Sa",

		Sunnuntai: "Su",
	},
	ee: {
		Esmaspäev: "Mo",

		Teisipäev: "Tu",

		Kolmapäev: "We",

		Neljapäev: "Th",

		Reede: "Fr",

		Laupäev: "Sa",

		Pühapäev: "Su",
	},
};

const toShortCodeDayName = (
	day: string | undefined | null,
): string | undefined => {
	const lang = locale.value as keyof typeof dayNameMapping;
	return dayNameMapping[lang]?.[
		day as keyof (typeof dayNameMapping)[typeof lang]
	];
};

const to24HourFormat = (timeStr: string): string | null => {
	try {
		const parsedTime = parse(
			timeStr,
			timeStr.includes(":") ? "H:mm" : "H",
			new Date(),
		);
		return format(parsedTime, "HH:mm");
	} catch {
		return null;
	}
};
const toOpeningTime = (timeStr: string | undefined | null) => {
	if (timeStr) {
		try {
			// We should have the starting and ending hour, they need to be formatted in 24:00 hour format
			const times = timeStr.trim().split("–");
			if (times.length === 2) {
				const [start, end] = times.map((time) => to24HourFormat(time));
				if (start && end) {
					return `${start}-${end}`;
				}
			}
		} catch {
			//
		}
	}
	return null;
};

const openingHours = computed<string | undefined>(() =>
	hoursBody.value?.tbody
		? hoursBody.value?.tbody
				.map((tr) => {
					const [dayCell, hoursCell] = tr.body;
					const dayShortCode = dayCell
						? toShortCodeDayName(dayCell.value)
						: undefined;
					if (dayShortCode && hoursCell) {
						const hours = toOpeningTime(hoursCell.value);
						// Only return entry if we can safely parse the day and opening duration
						if (hours) return `${dayShortCode} ${hours}`;
					}
					return null;
				})
				.filter((v: string | null) => !!v)
				.join(", ")
		: undefined,
);

const location = computed<Coordinates | null>(() => {
	if (outlet.value?.content?.location) {
		const loc = outlet.value.content.location.split(",");
		const [latitude, longitude] = loc;
		if (loc.length === 2 && latitude !== undefined && longitude !== undefined) {
			return {
				latitude: Number(latitude),
				longitude: Number(longitude),
			};
		}
	}
	return null;
});

useJsonld(() => {
	if (!outlet.value || !outlet.value?.content) {
		return null;
	}

	return {
		"@context": "https://schema.org",
		"@type": "FurnitureStore",
		name: outlet.value.name,
		address: {
			"@type": "PostalAddress",
			postalCode: outlet.value.content?.postal,
			streetAddress: outlet.value.content?.address,
			addressLocality: outlet.value.name,
			addressCountry: locale.value === "fi" ? "FI" : "EE",
		},
		telephone: outlet.value.content?.phone,
		brand: {
			"@type": "Brand",
			name: "Masku",
		},
		openingHours: openingHours.value,
		...(location.value && {
			hasMap: `https://google.com/maps?q=${location.value.latitude},${location.value.longitude}`,
			geo: {
				"@type": "GeoCoordinates",
				latitude: location.value.latitude,
				longitude: location.value.longitude,
			},
		}),
	};
});

useSeoMeta({
	title: outlet.value?.content?.meta?.title || outlet.value?.name,
	description: outlet.value?.content?.meta?.description ?? undefined,
	ogTitle: outlet.value?.content?.meta?.title || outlet.value?.name,
	ogDescription: outlet.value?.content?.meta?.description ?? undefined,
	ogImage: outlet.value?.content?.outlet_images[0]?.filename ?? undefined,
	twitterCard: "summary_large_image",
});
</script>

<i18n lang="json">
{
	"et": {
		"address": "Aadress",
		"phone-number": "Telefoni number",
		"email": "E-mail",
		"outlet-hours": "Lahtiolekuajad",
		"warehouse-hours": "Kättesaamine laost",
		"warehouse-ingress": "Nouda tooted kiireslt meie laost!",
		"trailer-rental": "Pakume ka haagiste renti!",
		"outlet-location": "Kaupluse asukoht",
		"product-count": "toodet",
		"outlet-products": "Kaupluse eripakkumised",
		"date-locale": "et-ET",
		"outlet-prefix": "Mööblipood Masku —",
		"outlet-warehouse-hours": "Ladu avatud",
		"exceptions": "Erandid",
		"socials-title": "Leia meid ka sotsiaalmeediast!"
	},
	"fi": {
		"address": "Osoite",
		"phone-number": "Puhelinnumero",
		"email": "Sähköposti",
		"outlet-hours": "Avoinna",
		"warehouse-hours": "Nouda tuotteet varastosta",
		"warehouse-ingress": "Nouda tuotteet nopeasti varastoltamme!",
		"trailer-rental": "Meiltä myös peräkärryn vuokraus!",
		"outlet-location": "Myymälän sijanti",
		"product-count": "tuotetta",
		"outlet-products": "Myymälän outlet tuotteet",
		"date-locale": "fi-FI",
		"outlet-prefix": "Huonekaluliike Masku",
		"outlet-warehouse-hours": "Varaston noutoajat",
		"exceptions": "Poikkeukset",
		"socials-title": "Löydät meidät myös somesta!"
	}
}
</i18n>
